import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./auth";
import { Star, Clock, BookOpen, ArrowUpRight, Check } from "lucide-react";
import * as Icons from "lucide-react";
import { inr, PACKAGES } from "@/data/content";
import { useEnquiry } from "./EnquiryDialog";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay }}>
    {children}
  </motion.div>
);

export const Section = ({ id, children, className = "" }) => (
  <section id={id} data-testid={`section-${id}`} className={`max-w-7xl mx-auto px-5 sm:px-8 py-8 md:py-12 ${className}`}>{children}</section>
);

export const Title = ({ eyebrow, title, highlight, text, center = true }) => (
  <Reveal className={`mb-8 max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
    <span className="eyebrow">{eyebrow}</span>
    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-[1.1]">
      {title}{" "}{highlight && <><br className="hidden sm:block" /><span className="neon-text">{highlight}</span></>}
    </h2>
    {text && <p className="mt-4 text-[var(--muted)] text-sm md:text-base leading-relaxed">{text}</p>}
  </Reveal>
);

export const Icon = ({ name, size = 22 }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C size={size} />;
};

export const Stars = ({ n }) => (
  <div className="flex gap-0.5 text-[var(--star)]">
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} fill={i <= Math.round(n) ? "currentColor" : "none"} />)}
  </div>
);

export const CourseCard = ({ c, i = 0 }) => (
  <Reveal delay={(i % 3) * 0.1}>
    <div data-testid={`course-card-${c.slug}`} className="glass neon-card rounded-3xl overflow-hidden h-full flex flex-col group">
      <div className="relative h-52 overflow-hidden">
        <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <span className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-xs font-bold text-[var(--pink)]">{c.category}</span>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center justify-between text-xs text-[var(--muted)]">
          <span className="flex items-center gap-1"><Clock size={13} /> {c.duration}</span>
          <span className="flex items-center gap-1"><BookOpen size={13} /> {c.lessons} lessons</span>
          <span className="flex items-center gap-1 text-[var(--star)]"><Star size={13} fill="currentColor" /> {c.rating}</span>
        </div>
        <h3 className="font-display text-xl font-semibold mt-3 leading-tight">{c.title}</h3>
        <p className="text-sm text-[var(--muted)] mt-2 flex-1">{c.short}</p>
        <div className="flex items-center justify-between mt-5">
          <div data-testid={`course-package-${c.slug}`}>
            <p className="text-[11px] text-[var(--muted)]">Included in</p>
            <p className="text-sm font-bold text-[var(--pink)]">{PACKAGES.find((p) => p.slug === c.package)?.name}</p>
          </div>
          <Link to={`/courses/${c.slug}`} data-testid={`course-explore-${c.slug}`} className="btn-ghost !py-2 !px-4 text-sm">Explore <ArrowUpRight size={15} /></Link>
        </div>
      </div>
    </div>
  </Reveal>
);

export const useBuyPackage = () => {
  const { open } = useEnquiry();
  const { user } = useAuth();
  const nav = useNavigate();
  return (p) => (user ? open("package", p.name) : nav(`/register?package=${p.slug}`));
};

export const PackageCard = ({ p, i = 0 }) => {
  const buy = useBuyPackage();
  return (
    <Reveal delay={i * 0.1}>
      <div data-testid={`package-card-${p.slug}`} className={`glass neon-card rounded-3xl p-7 h-full flex flex-col relative ${p.featured ? "border-[var(--pink)] shadow-[0_0_30px_-6px_var(--glow-pink)]" : ""}`}>
        <Link to={`/packages/${p.slug}`} className="block -mx-2 -mt-2 mb-5">
          <img data-testid={`package-image-${p.slug}`} src={p.image} alt={`${p.name} bundle`} loading="lazy" className="w-full aspect-[5/6] object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.35)] transition-transform duration-500 hover:scale-105" />
        </Link>
        <span className={`self-start rounded-full px-3 py-1 text-xs font-bold ${p.featured ? "bg-[var(--pink)] text-[var(--on-accent)]" : "glass text-[var(--pink)]"}`}>{p.tag}</span>
        <h3 className="font-display text-3xl font-bold mt-4">{p.name}</h3>
        <p className="text-sm text-[var(--muted)] mt-1">{p.text}</p>
        <div className="mt-5">
          <span className="text-4xl font-bold neon-text">{inr(p.price)}</span>
          <span className="text-sm line-through text-[var(--muted)] ml-2">{inr(p.oldPrice)}</span>
        </div>
        <ul className="mt-5 space-y-2 text-sm flex-1">
          {p.features.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-[var(--pink)] shrink-0 mt-0.5" /> {f}</li>)}
        </ul>
        <div className="flex gap-2 mt-6">
          <button data-testid={`package-buy-${p.slug}`} onClick={() => buy(p)} className="btn-neon flex-1 justify-center !px-4 text-sm">Buy Now</button>
          <Link to={`/packages/${p.slug}`} data-testid={`package-details-${p.slug}`} className="btn-ghost !px-4 text-sm">Details</Link>
        </div>
      </div>
    </Reveal>
  );
};

export const PageHero = ({ eyebrow, title, highlight, text }) => (
  <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-4">
    <Reveal>
      <span className="eyebrow">{eyebrow}</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 leading-[1.08]">{title} <span className="neon-text">{highlight}</span></h1>
      {text && <p className="mt-4 text-[var(--muted)] max-w-2xl">{text}</p>}
    </Reveal>
  </div>
);
